<template>
  <div
    id="git_instructions"
    :class="['flex flex-col proxima_nova', sc.outerGap]"
  >
    <div :class="['flex flex-col', sc.sectionGap]">
      <div :class="['flex flex-row items-center', sc.innerGap]">
        <div class="flex items-center bg_4 rounded-md p-1 border_8 cursor-pointer">
          <div
            @click="activeProtocol = 'https'"
            :class="[
              'px-4 py-1 rounded transition-all duration-200',
              sc.content,
              activeProtocol === 'https'
                ? 'bg_8 primary_color font-medium'
                : 'secondary_color'
            ]"
          >
            HTTPS
          </div>
          <div
            @click="activeProtocol = 'ssh'"
            :class="[
              'px-4 py-1 rounded transition-all duration-200',
              sc.content,
              activeProtocol === 'ssh'
                ? 'bg_8 primary_color font-medium'
                : 'secondary_color'
            ]"
          >
            SSH
          </div>
        </div>
      </div>

      <div id="clone_url_section" :class="['flex flex-col', sc.innerGap]">
        <span :class="[sc.label, 'secondary_color']">{{ activeProtocol.toUpperCase() }} URL</span>

        <div
          :class="[
            'flex flex-row items-center justify-between gap-2 rounded-lg border_8',
            sc.row,
          ]"
        >
          <span :class="[sc.content, 'truncate font-mono primary_color']">
            {{ currentCloneUrl }}
          </span>

          <ActionWrapper
            source="git_instructions"
            title="Copy"
            @click="copyToClipboard(currentCloneUrl)"
          >
            <ThemeImage
              :icon="imageNames.COPY_GREY"
              :class="sc.icon"
              alt="copy_icon"
            />
          </ActionWrapper>
        </div>
      </div>

      <div id="clone_this_repo_command" :class="['flex flex-col', sc.innerGap]">
        <div class="flex flex-row items-center justify-between">
          <span :class="[sc.label, 'secondary_color']">
            …clone this repository
          </span>

          <a-button v-if="activeProtocol === 'https'" :class="sc.button" @click="handleGenerateToken">
            Generate token
          </a-button>
          <a-button v-else :class="sc.button" @click="handleManageSshKeys">
            Manage SSH Keys
          </a-button>
        </div>

        <div
          :class="[
            'flex flex-row items-center justify-between gap-2 rounded-lg border_8',
            sc.row,
          ]"
        >
          <span :class="[sc.content, 'font-mono primary_color truncate min-w-0']">
            {{ currentCloneCommand }}
          </span>

          <ActionWrapper
            source="git_instructions"
            title="Copy"
            @click="copyToClipboard(currentCloneCommand)"
          >
            <ThemeImage
              :icon="imageNames.COPY_GREY"
              :class="sc.icon"
              alt="copy_icon"
            />
          </ActionWrapper>
        </div>
      </div>

      <div id="create_repo_command" :class="['flex flex-col', sc.innerGap]">
        <span :class="[sc.label, 'secondary_color']">
          …or create a new repository on the command line
        </span>

        <div :class="['rounded-lg border_8 font-mono', sc.content, sc.row]">
          <div class="flex items-start gap-2">
            <div class="flex-1 overflow-x-auto">
              <pre class="primary_color whitespace-pre m-0">{{ currentCreateCommands }}</pre>
            </div>

            <ActionWrapper
              source="git_instructions"
              title="Copy"
              @click="copyToClipboard(currentCreateCommands)"
            >
              <ThemeImage
                :icon="imageNames.COPY_GREY"
                :class="sc.icon"
                alt="copy_icon"
              />
            </ActionWrapper>
          </div>
        </div>
      </div>

      <div
        id="push_an_existing_repo_command"
        :class="['flex flex-col', sc.innerGap]"
      >
        <span :class="[sc.label, 'secondary_color']">
          …or push an existing repository from the command line
        </span>

        <div :class="['rounded-lg border_8 font-mono', sc.content, sc.row]">
          <div class="flex items-start gap-2">
            <div class="flex-1 overflow-x-auto">
              <pre class="primary_color whitespace-pre m-0">{{ currentPushCommands }}</pre>
            </div>

            <ActionWrapper
              source="git_instructions"
              title="Copy"
              @click="copyToClipboard(currentPushCommands)"
            >
              <ThemeImage
                :icon="imageNames.COPY_GREY"
                :class="sc.icon"
                alt="copy_icon"
              />
            </ActionWrapper>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { imageNames } from "@/src/constants/imageConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { getServiceConfig } from "@/src/config/transportFactory.js";
import { copyToClipboard } from "@/src/utils/utils.js";
import ActionWrapper from "@/src/components/custom_components/ActionWrapper.vue";
import {
  componentSizes,
  gitInstructionsSizeConfig,
} from "@/src/constants/customComponentConstants.js";

const props = defineProps({
  repo: {
    type: Object,
    required: true,
  },
  size: {
    type: String,
    default: "medium",
    validator: (v) => Object.values(componentSizes).includes(v),
  },
});

const router = useRouter();
const activeProtocol = ref('https');

const repoName = computed(() => props.repo?.name || "repository");
const workspaceId = computed(() => props.repo?.workspace_id || "workspace");
const repoSlug = computed(() => props.repo?.slug || "repo");
const gitBaseUrl = getServiceConfig("codebaseGit").baseUrl;
const sshHost = getServiceConfig("codebaseGitSsh").baseUrl;

const cloneUrl = computed(() => {
  return `${gitBaseUrl}/supergit/${workspaceId.value}/${repoSlug.value}.git`;
});

const sshCloneUrl = computed(() => {
  return `ssh://git@${sshHost}/${workspaceId.value}/${repoSlug.value}.git`;
});

const cloneCommand = computed(() => {
  return `git clone -c http.extraHeader="Authorization: Bearer <your_pat>" ${cloneUrl.value}`;
});

const sshCloneCommand = computed(() => {
  return `git clone ${sshCloneUrl.value}`;
});

const createNewRepoCommands = computed(() => {
  return `echo "# ${repoName.value}" >> README.md
git init
git add README.md
git commit -m "first commit"
git branch -M main
git remote add origin ${cloneUrl.value}
git config http.extraHeader "Authorization: Bearer <your_pat>"
git push -u origin main`;
});

const sshCreateNewRepoCommands = computed(() => {
  return `echo "# ${repoName.value}" >> README.md
git init
git add README.md
git commit -m "first commit"
git branch -M main
git remote add origin ${sshCloneUrl.value}
git push -u origin main`;
});

const pushExistingRepoCommands = computed(() => {
  return `git remote add origin ${cloneUrl.value}
git branch -M main
git config http.extraHeader "Authorization: Bearer <your_pat>"
git push -u origin main`;
});

const sshPushExistingRepoCommands = computed(() => {
  return `git remote add origin ${sshCloneUrl.value}
git branch -M main
git push -u origin main`;
});

const currentCloneUrl = computed(() =>
  activeProtocol.value === 'https' ? cloneUrl.value : sshCloneUrl.value
);

const currentCloneCommand = computed(() =>
  activeProtocol.value === 'https' ? cloneCommand.value : sshCloneCommand.value
);

const currentCreateCommands = computed(() =>
  activeProtocol.value === 'https' ? createNewRepoCommands.value : sshCreateNewRepoCommands.value
);

const currentPushCommands = computed(() =>
  activeProtocol.value === 'https' ? pushExistingRepoCommands.value : sshPushExistingRepoCommands.value
);

const sizeConfig = computed(() => gitInstructionsSizeConfig[props.size]);
const sc = sizeConfig;

const handleGenerateToken = () => {
  router.push("/settings#personal_tokens");
};

const handleManageSshKeys = () => {
  router.push("/settings#ssh_keys");
};
</script>

<style scoped>
pre {
  font-family: inherit;
}
</style>
